import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Stat, Switch, Badge, EmptyState } from '../components/ui.js';
import { Icon, BrandGlyph } from '../components/Icon.js';

interface Connection {
  meta_connection_id: string;
  page_name: string;
  ig_username: string | null;
  status: string;
}
interface PublishedPost {
  platform: 'instagram' | 'facebook';
  external_post_id: string;
  caption: string | null;
  image_url: string | null;
  permalink: string | null;
  timestamp: string | null;
  linked: boolean;
  post_id: string | null;
  product_ids: string[];
  auto_reply_enabled: boolean | null;
  auto_reply_template: string;
  public_reply_template: string;
  auto_reply_limit: number;
}
interface Product {
  product_id: string; title: string; primary_image_url: string | null;
}
interface Analytics {
  stats: { total: number; dm_sent: number; ignored: number; spam: number; errors: number };
}

const DM_TEMPLATES_AR = [
  'أهلاً {{name}}، {{product}} متوفر بسعر {{price}}. تحب أرسلك التفاصيل ونكمل الطلب؟',
  'شكراً لاهتمامك. {{product}} سعره {{price}} والتوصيل لكل المحافظات. وين توصل ونجهزه؟',
  '{{product}} متوفر بـ {{price}}. أرسل اسمك ورقمك لتثبيت الطلب.',
];
const DM_TEMPLATES_EN = [
  'Hi {{name}}, {{product}} is available for {{price}}. Want me to send details and lock in your order?',
  'Thanks for your interest. {{product}} costs {{price}}, we ship nationwide. Where should we deliver?',
  '{{product}} is in stock for {{price}}. Send your name + number to lock in the order.',
];
const PUBLIC_TEMPLATES_AR = [
  'أرسلنا لك التفاصيل بالخاص.',
  'تم الرد عليك بالخاص، تفقّد رسائلك.',
  'شكراً لتعليقك، راسلناك بالخاص.',
];
const PUBLIC_TEMPLATES_EN = [
  'We sent you the details via DM.',
  'Replied to you privately — check your DMs.',
  'Thanks for commenting, we DM\'d you.',
];

export function SocialPosts() {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useLang();
  const [platform, setPlatform] = useState<'all' | 'instagram' | 'facebook'>('all');
  const [active, setActive] = useState<PublishedPost | null>(null);

  const { data: conns, isLoading: connLoading } = useQuery({
    queryKey: ['meta-connections'],
    queryFn: () => api.get<{ connections: Connection[] }>('/meta-connections'),
  });
  const connected = (conns?.connections ?? []).some((c) => c.status === 'active');

  const { data: pub, isLoading } = useQuery({
    queryKey: ['social-published', platform],
    queryFn: () => api.get<{ posts: PublishedPost[]; connected: boolean }>(
      `/social-posts/published${platform === 'all' ? '' : `?platform=${platform}`}`,
    ),
    enabled: connected,
  });

  const { data: analytics } = useQuery({
    queryKey: ['social-analytics'],
    queryFn: () => api.get<Analytics>('/social-posts/analytics'),
    enabled: connected,
  });

  const { data: products } = useQuery({
    queryKey: ['products-for-tagging'],
    queryFn: () => api.get<{ items: Product[] }>('/products'),
    enabled: connected,
  });

  const posts = pub?.posts ?? [];

  return (
    <div className="wrap section-gap">
      <PageHead title={t('page.social.title')} sub={t('page.social.sub')} />

      {!connLoading && !connected ? (
        <EmptyState
          icon="plug"
          h={t('page.social.gate.h')}
          p={t('page.social.gate.p')}
          action={<button className="btn btn--primary" onClick={() => navigate('/channels')}><Icon name="plug" />{t('page.social.gate.cta')}</button>}
        />
      ) : (
        <>
          {analytics && (
            <div className="pgrid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
              <Stat icon="megaphone" label={t('page.social.stat.comments')} value={analytics.stats.total} />
              <Stat icon="send"      label={t('page.social.stat.dms')}      value={analytics.stats.dm_sent} />
              <Stat icon="inbox"     label={t('page.social.stat.ignored')}  value={analytics.stats.ignored} />
              <Stat icon="shield"    label={t('page.social.stat.spam')}     value={analytics.stats.spam} />
            </div>
          )}

          <div className="seg">
            {(['all', 'instagram', 'facebook'] as const).map((p) => (
              <button key={p} className={platform === p ? 'on' : ''} onClick={() => setPlatform(p)}>
                {p === 'all' ? t('common.all') : t(`channel.${p}`)}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="muted" style={{ padding: 24 }}>{t('page.social.loading')}</div>
          ) : posts.length === 0 ? (
            <EmptyState icon="image" h={t('page.social.empty.h')} p={t('page.social.empty.p')} />
          ) : (
            <div className="spgrid">
              {posts.map((post) => (
                <PostTile key={`${post.platform}:${post.external_post_id}`} post={post} onClick={() => setActive(post)} />
              ))}
            </div>
          )}
        </>
      )}

      {active && (
        <PostModal
          post={active}
          products={products?.items ?? []}
          onClose={() => setActive(null)}
        />
      )}
    </div>
  );
}

function PostTile({ post, onClick }: { post: PublishedPost; onClick: () => void }) {
  const { t } = useLang();
  const on = post.linked && post.auto_reply_enabled !== false;
  return (
    <button className="sptile" onClick={onClick}>
      {post.image_url ? <img src={post.image_url} alt="" /> : <div className="sptile__noimg"><Icon name="image" /></div>}
      <div className="sptile__grad" />
      <div className="sptile__top">
        <span className="sptile__plat"><BrandGlyph name={post.platform === 'instagram' ? 'ig' : 'fb'} size={15} /></span>
        {post.linked ? (
          <span className={`sptile__pill ${on ? 'sptile__pill--on' : 'sptile__pill--off'}`}>
            <Icon name="checkcircle" />{on ? t('page.social.tile.linked') : t('page.social.tile.off')}
          </span>
        ) : (
          <span className="sptile__pill sptile__pill--off"><Icon name="plus" />{t('page.social.tile.link')}</span>
        )}
      </div>
      <div className="sptile__bot">
        {post.caption && <div className="sptile__cap">{post.caption}</div>}
        {post.product_ids.length > 0 && (
          <span className="sptile__pill sptile__pill--off"><Icon name="box" />{post.product_ids.length} {t('page.social.tile.products')}</span>
        )}
      </div>
    </button>
  );
}

function PostModal({ post, products, onClose }: { post: PublishedPost; products: Product[]; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const { t, lang } = useLang();
  const [selected, setSelected] = useState<string[]>(post.product_ids);
  const [enabled, setEnabled] = useState<boolean>(post.auto_reply_enabled !== false);
  const [limit, setLimit] = useState<number>(post.auto_reply_limit || 5);
  const [dm, setDm] = useState<string>(post.auto_reply_template);
  const [pubReply, setPubReply] = useState<string>(post.public_reply_template);

  const dmTemplates = lang === 'ar' ? DM_TEMPLATES_AR : DM_TEMPLATES_EN;
  const publicTemplates = lang === 'ar' ? PUBLIC_TEMPLATES_AR : PUBLIC_TEMPLATES_EN;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['social-published'] });
    qc.invalidateQueries({ queryKey: ['social-analytics'] });
  };

  const save = useMutation({
    mutationFn: () =>
      api.post('/social-posts', {
        platform: post.platform,
        external_post_id: post.external_post_id,
        product_ids: selected,
        caption: post.caption,
        image_url: post.image_url,
        permalink: post.permalink,
        auto_reply_enabled: enabled,
        auto_reply_template: dm,
        public_reply_template: pubReply,
        auto_reply_limit: limit,
      }),
    onSuccess: () => { refresh(); onClose(); },
  });

  const unlink = useMutation({
    mutationFn: () => api.delete(`/social-posts/${post.post_id}`),
    onSuccess: () => { refresh(); onClose(); },
  });

  return (
    <div className="modal__scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__head">
          {post.image_url ? <img className="modal__thumb" src={post.image_url} alt="" /> : <div className="modal__thumb" />}
          <div style={{ minWidth: 0 }}>
            <div className="flex aic gap8" style={{ marginBottom: 4 }}>
              <BrandGlyph name={post.platform === 'instagram' ? 'ig' : 'fb'} size={15} />
              <span style={{ fontWeight: 700, fontSize: 14 }}>{t(`channel.${post.platform}`)}</span>
              {post.permalink && (
                <a href={post.permalink} target="_blank" rel="noreferrer" className="muted flex aic" style={{ fontSize: 12, gap: 4 }}>
                  {t('page.social.modal.open')} <Icon name="external" size={12} />
                </a>
              )}
            </div>
            <div className="muted" style={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {post.caption || t('page.social.modal.noCap')}
            </div>
          </div>
          <button className="modal__x" onClick={onClose} aria-label={t('common.close')} style={{ fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        <div className="modal__body">
          <div className="field">
            <label className="field__label">{t('page.social.modal.products')}</label>
            <ProductPicker products={products} selected={selected} setSelected={setSelected} />
            {selected.length === 0 && <div className="field__hint">{t('page.social.modal.productsHint')}</div>}
          </div>

          <div className="field">
            <div className="between">
              <div>
                <label className="field__label" style={{ marginBottom: 2 }}>{t('page.social.modal.enable')}</label>
                <div className="field__hint" style={{ marginTop: 0 }}>{t('page.social.modal.enableHint')}</div>
              </div>
              <Switch on={enabled} onClick={() => setEnabled((v) => !v)} />
            </div>
          </div>

          <div className="field">
            <label className="field__label">{t('page.social.modal.limit')}</label>
            <div className="rangerow">
              <input className="range" type="range" min={1} max={20} value={limit} onChange={(e) => setLimit(Number(e.target.value))} />
              <span className="rangeval">{limit}</span>
            </div>
            <div className="field__hint">{t('page.social.modal.limitHint')}</div>
          </div>

          <div className="field">
            <label className="field__label">{t('page.social.modal.dm')}</label>
            <div className="tmpls">
              {dmTemplates.map((tpl, i) => (
                <button key={i} type="button" className="tmpl" onClick={() => setDm(tpl)}>{t('page.social.modal.template')} {i + 1}</button>
              ))}
            </div>
            <textarea
              className="textarea"
              rows={3}
              value={dm}
              onChange={(e) => setDm(e.target.value)}
              placeholder={t('page.social.modal.dmPh')}
            />
            <div className="field__hint placeholder-hint">{t('page.social.modal.placeholders')}</div>
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <label className="field__label">{t('page.social.modal.public')}</label>
            <div className="tmpls">
              {publicTemplates.map((tpl, i) => (
                <button key={i} type="button" className="tmpl" onClick={() => setPubReply(tpl)}>{t('page.social.modal.template')} {i + 1}</button>
              ))}
            </div>
            <input
              className="input"
              value={pubReply}
              onChange={(e) => setPubReply(e.target.value)}
              placeholder={t('page.social.modal.publicPh')}
            />
          </div>
        </div>

        <div className="modal__foot">
          <button className="btn btn--primary" onClick={() => save.mutate()} disabled={save.isPending}>
            <Icon name="check" />{save.isPending ? t('common.saving') : t('page.social.modal.save')}
          </button>
          <button className="btn btn--ghost" onClick={onClose}>{t('common.cancel')}</button>
          {post.post_id && (
            <button className="btn btn--danger" style={{ marginInlineStart: 'auto' }} onClick={() => unlink.mutate()} disabled={unlink.isPending}>
              <Icon name="trash" />{t('page.social.modal.unlink')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductPicker({ products, selected, setSelected }: { products: Product[]; selected: string[]; setSelected: (ids: string[]) => void }) {
  const { t } = useLang();
  const [q, setQ] = useState('');
  const filtered = products.filter((p) => p.title.toLowerCase().includes(q.toLowerCase())).slice(0, 40);
  const toggle = (id: string) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="ppick">
      <div className="inbox__search" style={{ margin: 0 }}>
        <Icon name="search" />
        <input placeholder={t('page.social.search.products.ph')} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="ppick__list">
        {filtered.map((p) => {
          const on = selected.includes(p.product_id);
          return (
            <button key={p.product_id} type="button" className={`ppick__item ${on ? 'ppick__item--on' : ''}`} onClick={() => toggle(p.product_id)}>
              {p.primary_image_url ? <img src={p.primary_image_url} alt="" /> : <span className="modal__thumb" style={{ width: 28, height: 28 }} />}
              <span>{p.title}</span>
              {on && <span className="ck"><Icon name="check" /></span>}
            </button>
          );
        })}
        {filtered.length === 0 && <div className="muted" style={{ gridColumn: '1 / -1', padding: 12, fontSize: 12.5 }}>{t('page.social.search.products.empty')}</div>}
      </div>
    </div>
  );
}
